import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../context/LanguageContext';
import { Carousel, IconButton, Typography, Button } from "@material-tailwind/react";

// ==================== CONSTANTES ====================
const CAROUSEL_CONFIG = {
  transition: { duration: 2 },
  loop: true,
};

const HEIGHTS = {
  mobile: 'h-[650px]',
  sm: 'sm:h-[400px]',
  md: 'md:h-[500px]',
  lg: 'lg:h-[650px]',
};

const STYLES = {
  carousel: "w-full h-auto overflow-hidden shadow-lg",
  slide: `relative w-full ${HEIGHTS.mobile} ${HEIGHTS.sm} ${HEIGHTS.md} ${HEIGHTS.lg}`,
  image: "absolute inset-0 w-full h-full object-cover",
  overlay: "absolute inset-0 flex flex-col items-end justify-center bg-black/30 p-4 md:p-8 lg:p-12 text-right pr-6 md:pr-12 lg:pr-20",
  title: "mb-2 text-xl text-right md:text-3xl lg:text-5xl font-bold max-w-[80%] md:max-w-[50%]",
  description: "mb-4 opacity-90 text-sm text-right md:text-lg max-w-[80%] md:max-w-[50%]",
  arrowButton: "!absolute top-2/4 -translate-y-2/4",
  navigation: "absolute bottom-4 left-2/4 z-50 flex -translate-x-2/4 gap-2",
  dot: {
    base: "block h-1 cursor-pointer rounded-2xl transition-all",
    active: "w-8 bg-white",
    inactive: "w-4 bg-white/50"
  }
};

// ==================== ICONOS SVG ====================
const ArrowIcon = ({ direction = "left" }) => {
  const paths = {
    left: "M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18",
    right: "M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
  };

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      strokeWidth={2} 
      stroke="currentColor" 
      className="h-6 w-6"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={paths[direction]} />
    </svg>
  );
};

// ==================== COMPONENTES ====================
const NavigationButton = ({ onClick, direction, position }) => (
  <IconButton
    variant="text"
    color="white"
    size="lg"
    onClick={onClick}
    className={`${STYLES.arrowButton} ${position}`}
    aria-label={`${direction === 'left' ? 'Anterior' : 'Siguiente'} imagen`}
  >
    <ArrowIcon direction={direction} />
  </IconButton>
);

const NavigationDots = ({ setActiveIndex, activeIndex, length }) => (
  <div className={STYLES.navigation}>
    {Array.from({ length }).map((_, index) => (
      <span
        key={index}
        className={`${STYLES.dot.base} ${
          activeIndex === index ? STYLES.dot.active : STYLES.dot.inactive
        }`}
        onClick={() => setActiveIndex(index)}
        role="button"
        aria-label={`Ir a imagen ${index + 1}`}
        aria-current={activeIndex === index ? "true" : "false"}
      />
    ))}
  </div>
);

const SlideContent = ({ image, index, showButton, buttonText }) => (
  <div className={STYLES.slide}>
    <img
      src={image.src}
      alt={image.alt || `Imagen ${index + 1}`}
      className={STYLES.image}
      loading={index === 0 ? "eager" : "lazy"}
    />
    
    <div className={STYLES.overlay}>
      <Typography
        variant="h1"
        color="white"
        className={STYLES.title}
      >
        {image.title || "Título de la imagen"}
      </Typography>
      
      <Typography
        variant="lead"
        color="white"
        className={STYLES.description}
      >
        {image.description || "Descripción corta de la imagen."}
      </Typography>
      
      {showButton && (
        <Link to="/que-hacemos" className="text-white">
          <Button 
            size="md" 
            className="bg-primary hover:bg-green-700 transition-colors duration-300"
          >
            {buttonText}
          </Button>
        </Link>
      )}
    </div>
  </div>
);

// ==================== COMPONENTE PRINCIPAL ====================
const CarouselImg = ({ images }) => {
  const { language, translations } = useContext(LanguageContext);
  const t = translations[language];

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="flex justify-center items-center w-full">
      <Carousel
        {...CAROUSEL_CONFIG}
        className={STYLES.carousel}
        prevArrow={({ handlePrev }) => (
          <NavigationButton 
            onClick={handlePrev} 
            direction="left" 
            position="left-4" 
          />
        )}
        nextArrow={({ handleNext }) => (
          <NavigationButton 
            onClick={handleNext} 
            direction="right" 
            position="!right-4" 
          />
        )}
        navigation={({ setActiveIndex, activeIndex, length }) => (
          <NavigationDots 
            setActiveIndex={setActiveIndex}
            activeIndex={activeIndex}
            length={length}
          />
        )}
      >
        {images.map((image, index) => (
          <SlideContent
            key={`slide-${index}`}
            image={image}
            index={index}
            showButton={index === 0}
            buttonText={t.buttonText1}
          />
        ))}
      </Carousel>
    </div>
  );
};

export default CarouselImg;
